import * as prismic from '@prismicio/client';
import ErrorPage from 'next/error';
import { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from 'next';
import { useRouter } from 'next/compat/router';

// UI
import BlogAuthorPage from '../../../components/pages/BlogAuthorPage';
import BlogCategoryPage from '../../../components/pages/BlogCategoryPage';
import BlogPostPage from '../../../components/pages/BlogPostPage';

// CMS
import {
  getBlogPostCategoryAuthor,
  getProductIdsFromPost,
  BlogPostCategoryAndAuthorType,
  BlogPostPageType,
  BlogCategoryPageType,
  CMSBlogPostSearchType,
  getProductIdsFromPage,
  BlogAuthorPageType,
} from '@virginexperiencedays/cms';
import { blogListingContentRelationship } from '@virginexperiencedays/cms/constants';

// Product API
import { VEDBackendApi, type BlogProduct } from '@virginexperiencedays/backend';
import { mapToId } from '@virginexperiencedays/products/src/util';

// utils
import { createPagination } from '../../../utils/createPagination';
import { getTopProducts } from '../../../utils/getTopProducts';

// types
import type { Product, ProductIdMapType } from '@virginexperiencedays/products';
import { createClient } from '../../../prismicio';
import { createPostsSearchArray } from '../../../utils/createPostsSearchArray';
import { ISR_CONTROL } from '../../../constants/revalidate';
import { fetchVedNavigationItems } from '../../../utils/precache/navigation-items';
import type { BaseNonPrismicPageProps } from '../../../components/pages/types';
import { PAGE_TYPES } from '../../../components/tracking/gtm/consts';

type PostAndListingPageProps = {
  preview: boolean;
  blog_post_page: BlogPostPageType | null;
  products: ProductIdMapType | null;
  category: BlogCategoryPageType | null;
  categoryPosts: CMSBlogPostSearchType | null;
  blog_author_page: BlogAuthorPageType | null;
  authorPosts: CMSBlogPostSearchType | null;
  currentPage: number;
  totalPosts: number;
  hasNextPage: boolean;
} & BaseNonPrismicPageProps;

export const commonCrumbs = [
  { name: 'Home', path: process.env.NEXT_PUBLIC_HOME_LINK, externalLink: true },
  { name: 'Blog', path: '/experience-blog' },
];

export const postsPerPage = 18;

const Page = ({
  blog_post_page,
  products,
  category,
  categoryPosts,
  blog_author_page,
  authorPosts,
  currentPage,
  totalPosts,
  hasNextPage,
}: PostAndListingPageProps) => {
  const router = useRouter();

  if (
    !router.isFallback &&
    !(blog_post_page?._meta?.uid || category?._meta?.uid || blog_author_page?._meta?.uid)
  ) {
    return <ErrorPage statusCode={404} />;
  }

  if (router.isFallback) {
    return <div>Loading ...</div>;
  }

  return (
    <>
      {blog_post_page ? (
        <BlogPostPage post={blog_post_page} products={products} commonCrumbs={commonCrumbs} />
      ) : null}
      {category ? (
        <BlogCategoryPage
          products={products}
          commonCrumbs={commonCrumbs}
          category={category}
          currentPage={currentPage}
          totalPosts={totalPosts}
          postsPerPage={postsPerPage}
          categoryPosts={categoryPosts?.map(({ node }) => node)}
          isLandingPage={true}
          hasPrevPage={false}
          hasNextPage={hasNextPage}
        />
      ) : null}
      {blog_author_page ? (
        <BlogAuthorPage
          products={products}
          commonCrumbs={commonCrumbs}
          currentPage={currentPage}
          totalPosts={totalPosts}
          postsPerPage={postsPerPage}
          blog_author_page={blog_author_page}
          authorPosts={authorPosts?.map(({ node }) => node)}
          isLandingPage={true}
          hasPrevPage={false}
          hasNextPage={hasNextPage}
        />
      ) : null}
    </>
  );
};

const navigationServiceUrl = process?.env?.NEXT_PUBLIC_NAVIGATION_SERVICE_BASE_URL;
export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
  previewData,
}: GetStaticPropsContext) => {
  const topProducts = await getTopProducts();

  const data: BlogPostCategoryAndAuthorType = await getBlogPostCategoryAuthor(
    params.slug,
    previewData
  );
  if (data.category === null && data.blog_post_page === null && data.blog_author_page === null) {
    return {
      notFound: true,
    };
  }
  const initialNavItems = await fetchVedNavigationItems(navigationServiceUrl);

  let categoryPosts = null;
  let authorPosts = null;
  let products: BlogProduct[] = null;
  let totalPosts = 0;
  let hasNextPage = false;

  const api = new VEDBackendApi();
  const prismicClient = createClient();

  let trackingPageType = PAGE_TYPES.BlogPost;

  if (data?.blog_author_page) {
    trackingPageType = PAGE_TYPES.BlogAuthor;
    const postsByAuthor = await prismicClient.getAllByType('blog_post_page', {
      orderings: { field: 'my.blog_post_page.date', direction: 'desc' },
      predicates: [
        prismic.predicate.at('my.blog_post_page.author', data.blog_author_page._meta?.id),
      ],
      fetchLinks: [...blogListingContentRelationship],
    });
    const mappedPosts = createPostsSearchArray(postsByAuthor);

    const productIds = getProductIdsFromPage(data.blog_author_page?.editors_picks);
    const filteredIds = productIds
      .filter((productId) => Number.isInteger(parseInt(productId)))
      .map((productId) => productId.toString());

    if (filteredIds?.length)
      products = (await api.bff.blogProducts.find({ id: filteredIds }))?.items || [];

    // @TODO: Refactor to use @prismic/client ? so we'll be able to ask for specific page instead of this manual process
    if (mappedPosts.length > 0) {
      authorPosts = createPagination(mappedPosts, postsPerPage)[1];
    }
    totalPosts = mappedPosts.length;
    hasNextPage = totalPosts > postsPerPage;
  }

  if (data?.category) {
    trackingPageType = PAGE_TYPES.BlogCategory;
    const postsByCategory = await prismicClient.getAllByType('blog_post_page', {
      orderings: { field: 'my.blog_post_page.date', direction: 'desc' },
      predicates: [prismic.predicate.at('my.blog_post_page.parent_link', data.category._meta.id)],
      fetchLinks: [...blogListingContentRelationship],
    });
    const mappedPosts = createPostsSearchArray(postsByCategory);

    const productIds = getProductIdsFromPage(data.category?.editors_picks);
    const filteredIds = productIds
      .filter((productId) => Number.isInteger(parseInt(productId)))
      .map((productId) => productId.toString());

    if (filteredIds?.length)
      products = (await api.bff.blogProducts.find({ id: filteredIds }))?.items || [];

    // Pagination dictionary
    // @TODO: Refactor to use @prismic/client ? so we'll be able to ask for specific page instead of this manual process
    if (mappedPosts.length > 0) {
      categoryPosts = createPagination(mappedPosts, postsPerPage)[1];
    }

    totalPosts = mappedPosts.length;
    hasNextPage = totalPosts > postsPerPage;
  }
  if (data?.blog_post_page) {
    if (
      data.blog_post_page.body == null ||
      data.blog_post_page.title == null ||
      data.blog_post_page.author == null
    ) {
      return {
        notFound: true,
      };
    }

    const productIds = getProductIdsFromPost(data?.blog_post_page);
    const filteredIds = productIds
      .filter((productId) => Number.isInteger(parseInt(productId)))
      .map((productId) => productId.toString());

    if (filteredIds?.length)
      products = (await api.bff.blogProducts.find({ id: filteredIds }))?.items || [];
  }

  return {
    props: {
      trackingPageType,
      preview,
      blog_post_page: data?.blog_post_page ?? null,
      // TODO LP-7094: use an interface and migrate off old Product API types
      products: mapToId(products as unknown as Product[]),
      category: data?.category ?? null,
      categoryPosts,
      blog_author_page: data?.blog_author_page ?? null,
      authorPosts,
      // This will always be landing page, other pages are handled by rewrites and it's destination is [slug]/pages/[page]
      currentPage: 1,
      totalPosts,
      hasNextPage,
      topProducts,
      initialNavItems,
    },
    revalidate: ISR_CONTROL.ONCE_PER_DAY,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export default Page;
